// src/pages/HomeworkWhatIf.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { costAPI, homeworkAPI } from '../services/api';
import CostBarChart from '../components/CostBarChart';

// --- Client-side Cost Calculation Logic ---
let USD_TO_GBP_RATE = 0.76;
let MODEL_PRICES = {};
let IMAGE_PRICES = {};
let AUDIO_PRICES = {};

function calculateTokenCost(modelName, inputTokens, outputTokens) {
    const pricing = MODEL_PRICES[modelName];
    if (!pricing) return 0;
    let rates;
    if (pricing.small && pricing.large) {
        rates = (inputTokens <= pricing.small.threshold) ? pricing.small : pricing.large;
    } else {
        rates = pricing;
    }
    const inputCostUsd = (inputTokens / 1000000) * rates.input;
    const outputCostUsd = (outputTokens / 1000000) * rates.output;
    const costInUsd = inputCostUsd + outputCostUsd;
    return costInUsd * USD_TO_GBP_RATE;
}

function calculateImageCost(modelName, imageCount) {
    const rateUsd = IMAGE_PRICES[modelName];
    if (!rateUsd) return 0;
    const costInUsd = rateUsd * imageCount;
    return costInUsd * USD_TO_GBP_RATE;
}

function calculateAudioCost(modelName, characterCount) {
    const rateUsd = AUDIO_PRICES[modelName];
    if (!rateUsd) return 0;
    const costInUsd = (characterCount / 1000000) * rateUsd;
    return costInUsd * USD_TO_GBP_RATE;
}

// --- +++ NEW: Model Display Name Mapping +++ ---
const MODEL_DISPLAY_NAMES = {
    // Text Models
    'gemini-2.5-pro': 'Gemini 2.5 Pro',
    'gemini-pro-latest': 'Gemini 2.5 Pro (Latest)',
    'gemini-2.5-computer-use-preview-10-2025': 'Gemini 2.5 Computer Use',
    'gemini-2.5-flash': 'Gemini 2.5 Flash',
    'gemini-2.5-flash-preview-09-2025': 'Gemini 2.5 Flash (Preview)',
    'gemini-2.5-flash-native-audio-preview-09-2025': 'Gemini 2.5 Flash Audio',
    'gemini-2.5-flash-lite': 'Gemini 2.5 Flash-Lite',
    'gemini-2.5-flash-lite-preview-09-2025': 'Gemini 2.5 Flash-Lite (Preview)',
    'gemini-2.0-flash': 'Gemini 2.0 Flash',
    'gemini-2.0-flash-lite': 'Gemini 2.0 Flash-Lite',

    // Image Models (Standard)
    'runware:100@1': 'Runware (SDXL 1.0)',
    'runware:101@1': 'Runware (Flux.1 Dev)',
    
    // Image Models (New Cheap Options)
    'runware:111@1': 'Runware (Flux.1 SRPO)',
    'runware:68487@0862923414': 'Runware (SDXL Alt)',
    'runware:34@1': 'Runware (Pony v6)',
    'runware:4@1': 'Runware (Cartoon 3D)',
    'runware:108@1': 'Runware (Qwen-Image)',
    'runware:107@1': 'Runware (Flux.1 Krea)',

    'ideogram:4@1': 'Ideogram 2.0',
    'bria:10@1': 'Runware (Bria 2.3)',
    'imagen-4.0-ultra-generate-001': 'Imagen 4 Ultra',
    'imagen-4.0-generate-001': 'Imagen 4 Standard',
    'imagen-4.0-fast-generate-001': 'Imagen 4 Fast',
    'imagen-3.0-generate-002': 'Imagen 3',
    'gemini-2.5-flash-image': 'Gemini 2.5 Flash (Image)',
    
    // Audio Models
    'Standard-Cloud-TTS': 'Standard Cloud TTS',
    'en-GB-Chirp3-HD-Charon': 'Google Chirp 3 (Charon)',
    'en-GB-Chirp3-HD-Vindemiatrix': 'Google Chirp 3 (Vindemiatrix)',
    'en-GB-Chirp3-HD-Achird': 'Google Chirp 3 (Achird)'
};
// --- +++ END OF MAPPING +++ ---

// --- +++ MOVED CONSTANTS OUTSIDE COMPONENT TO SATISFY ESLINT +++ ---
const CURATED_IMAGE_MODELS = [
    'runware:100@1',  // SDXL 1.0
    'runware:111@1',  // Flux SRPO
    'runware:68487@0862923414', // SDXL Alt
    'runware:34@1',   // Pony
    'runware:4@1',    // Cartoon
    'runware:108@1',  // Qwen
    'runware:107@1',  // Flux Krea
    'runware:101@1',  // Flux Dev Standard
    'ideogram:4@1',
    'imagen-4.0-ultra-generate-001',
    'bria:10@1',
    'imagen-4.0-generate-001',
    'gemini-2.5-flash-image',
    'gemini-2.0-flash',
    'imagen-3.0-generate-002',
    'imagen-4.0-fast-generate-001'
];

const CURATED_TEXT_MODELS = [
    'gemini-2.5-pro',
    'gemini-2.5-flash',
    'gemini-2.0-flash'
];
// -------------------------------------

// --- Helper: Find Cheapest Model ---
function findCheapestModel(summary, models, type) {
    if (!models || models.length === 0) {
        return summary.models[0]?.model || '';
    }
    let cheapestModel = '';
    let minCost = Infinity;
    for (const modelName of models) {
        let currentCost = 0;
        if (type === 'token') {
            currentCost = calculateTokenCost(modelName, summary.totalInput, summary.totalOutput);
        } else if (type === 'image') {
            currentCost = calculateImageCost(modelName, summary.totalImage);
        } else if (type === 'audio') {
            currentCost = calculateAudioCost(modelName, summary.totalChar);
        }
        if (currentCost < minCost) {
            minCost = currentCost;
            cheapestModel = modelName;
        }
    }
    return cheapestModel || models[0] || '';
}


// --- Helper Component: WhatIfCostBox ---
const WhatIfCostBox = ({ 
    title, 
    summary, 
    type, 
    models, 
    selectedModel, 
    onModelChange, 
    whatIfCost 
}) => {
    const actualCost = summary.totalCost;
    const difference = whatIfCost - actualCost;
    
    // If diff is negligible (floating point issues), treat as 0
    const displayDiff = Math.abs(difference) < 0.000001 ? 0 : difference;
    
    const diffColor = displayDiff > 0 ? '#dc3545' : (displayDiff < 0 ? '#28a745' : '#555');

    return (
        <div style={{ flex: '1 1 300px', border: '1px solid #ccc', padding: '15px', borderRadius: '8px', background: '#f9f9f9' }}>
            <h3 style={{ marginTop: 0 }}>{title}</h3>
            
            <p style={{ margin: '5px 0' }}>
                Actual Cost: <strong>£{actualCost.toFixed(6)}</strong>
            </p>
            <div style={{ fontSize: '0.9em', color: '#555', paddingLeft: '10px' }}>
                {summary.totalInput > 0 && <p style={{ margin: '2px 0' }}>Input Tokens: {summary.totalInput.toLocaleString()}</p>}
                {summary.totalOutput > 0 && <p style={{ margin: '2px 0' }}>Output Tokens: {summary.totalOutput.toLocaleString()}</p>}
                {summary.totalImage > 0 && <p style={{ margin: '2px 0' }}>Images: {summary.totalImage.toLocaleString()}</p>}
                {summary.totalChar > 0 && <p style={{ margin: '2px 0' }}>Characters: {summary.totalChar.toLocaleString()}</p>}
                <p style={{ margin: '2px 0' }}>Models Used: {summary.models?.map(m => MODEL_DISPLAY_NAMES[m.model] || m.model).join(', ') || 'N/A'}</p>
            </div>

            <hr style={{ margin: '15px 0' }} />

            <h4 style={{ marginBottom: '10px' }}>What-If Scenario</h4>
            <div style={{ marginBottom: '10px' }}>
                <label htmlFor={`model-select-${title}`} style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    If I used this model:
                </label>
                <select 
                    id={`model-select-${title}`}
                    value={selectedModel || ''}
                    onChange={(e) => onModelChange(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    disabled={!models || models.length === 0}
                >
                    <option value="" disabled>Select a model</option>
                    {(models || []).map(modelName => (
                        <option key={modelName} value={modelName}>
                            {/* +++ MODIFIED: Use friendly display name +++ */}
                            {MODEL_DISPLAY_NAMES[modelName] || modelName}
                        </option>
                    ))}
                </select>
            </div>

            <p style={{ margin: '5px 0' }}>
                Potential Cost: <strong>£{whatIfCost.toFixed(6)}</strong>
            </p>
            <p style={{ margin: '5px 0', color: diffColor, fontWeight: 'bold' }}>
                Difference: {displayDiff > 0 ? '+' : ''}£{displayDiff.toFixed(6)}
            </p>
        </div>
    );
};

// --- Main Page Component ---
const HomeworkWhatIf = () => {
    const { homeworkId } = useParams();
    const [homeworkCostSummary, setHomeworkCostSummary] = useState({ totalCost: 0, breakdown: [] });
    const [lessonCostSummary, setLessonCostSummary] = useState({ totalCost: 0, breakdown: [] });
    const [pricingData, setPricingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const homeworkRes = await homeworkAPI.getById(homeworkId);
                const lessonPlanId = homeworkRes.data.lessonPlanId;
                if (!lessonPlanId) {
                    throw new Error("Lesson Plan ID not found on homework object.");
                }
                const [summaryRes, pricingRes, lessonSummaryRes] = await Promise.all([
                    costAPI.getHomeworkSummary(homeworkId),
                    costAPI.getPricing(),
                    costAPI.getLessonPlanSummary(lessonPlanId)
                ]);
                
                setHomeworkCostSummary(summaryRes.data);
                setLessonCostSummary(lessonSummaryRes.data);
                setPricingData(pricingRes.data);

                USD_TO_GBP_RATE = pricingRes.data.usdToGbpRate;
                MODEL_PRICES = pricingRes.data.tokenModels;
                IMAGE_PRICES = pricingRes.data.imageModels;
                AUDIO_PRICES = pricingRes.data.audioModels;

            } catch (err) {
                setError('Failed to load cost data.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [homeworkId]);

    const getHomeworkSummaryData = useCallback((apiCallTypes) => {
        const types = Array.isArray(apiCallTypes) ? apiCallTypes : [apiCallTypes];
        let totalCost = 0, totalInput = 0, totalOutput = 0, totalImage = 0, totalChar = 0;
        let models = new Map();
        const dataItems = homeworkCostSummary.breakdown.filter(b => types.includes(b.apiCallType));
        if (dataItems.length === 0) return { totalCost: 0, totalInput: 0, totalOutput: 0, totalImage: 0, totalChar: 0, models: [] };
        for (const item of dataItems) {
            totalCost += item.totalCost;
            for (const modelItem of item.modelBreakdown) {
                const existing = models.get(modelItem.model) || { inputTokens: 0, outputTokens: 0, imageCount: 0, characterCount: 0, count: 0 };
                models.set(modelItem.model, {
                    inputTokens: existing.inputTokens + modelItem.inputTokens,
                    outputTokens: existing.outputTokens + modelItem.outputTokens,
                    imageCount: existing.imageCount + (modelItem.imageCount || 0),
                    characterCount: existing.characterCount + (modelItem.characterCount || 0),
                    count: existing.count + modelItem.count
                });
            }
        }
        const modelArray = [];
        for (const [modelName, data] of models.entries()) {
            totalInput += data.inputTokens; totalOutput += data.outputTokens; totalImage += data.imageCount; totalChar += data.characterCount;
            modelArray.push({ model: modelName, ...data });
        }
        return { totalCost, totalInput, totalOutput, totalImage, totalChar, models: modelArray };
    }, [homeworkCostSummary]);

    const getLessonSummaryData = useCallback((apiCallType) => {
        const data = lessonCostSummary.breakdown.find(b => b.apiCallType === apiCallType);
        if (!data) return { totalCost: 0, totalInput: 0, totalOutput: 0, models: [] };
        const totalInput = data.modelBreakdown.reduce((acc, m) => acc + m.inputTokens, 0);
        const totalOutput = data.modelBreakdown.reduce((acc, m) => acc + m.outputTokens, 0);
        return { totalCost: data.totalCost, totalInput: totalInput, totalOutput: totalOutput, models: data.modelBreakdown };
    }, [lessonCostSummary]);

    const extractionSummary = useMemo(() => getLessonSummaryData('extractTextFromDocument'), [getLessonSummaryData]);
    const analysisSummary = useMemo(() => getLessonSummaryData('generateLessonAnalysis'), [getLessonSummaryData]);
    const masterTutorialSummary = useMemo(() => getHomeworkSummaryData('generateTutorialContent'), [getHomeworkSummaryData]);
    const studentTutorialSummary = useMemo(() => getHomeworkSummaryData('generatePersonalizedChunk'), [getHomeworkSummaryData]);
    const scriptSummary = useMemo(() => getHomeworkSummaryData('generateStudentSlideshowScript'), [getHomeworkSummaryData]);
    const imageSummary = useMemo(() => getHomeworkSummaryData('generateImageFromPrompt'), [getHomeworkSummaryData]);
    const audioSummary = useMemo(() => getHomeworkSummaryData('generateAudioFromText'), [getHomeworkSummaryData]);

    const [extractionModel, setExtractionModel] = useState(null);
    const [analysisModel, setAnalysisModel] = useState(null);
    const [masterTutorialModel, setMasterTutorialModel] = useState(null);
    const [studentTutorialModel, setStudentTutorialModel] = useState(null);
    const [scriptModel, setScriptModel] = useState(null);
    const [imageModel, setImageModel] = useState(null);
    const [audioModel, setAudioModel] = useState(null);

    // +++ MODIFIED: Use curated lists to filter models +++
    const tokenModelsList = useMemo(() => {
        if (!pricingData) return [];
        // Return only models present in both pricing data AND our curated list
        return CURATED_TEXT_MODELS.filter(model => pricingData.tokenModels[model]);
    }, [pricingData]);

    const imageModelsList = useMemo(() => {
        if (!pricingData) return [];
        return CURATED_IMAGE_MODELS.filter(model => pricingData.imageModels[model]);
    }, [pricingData]);

    const audioModelsList = useMemo(() => {
        if (!pricingData) return [];
        return Object.keys(pricingData.audioModels || {});
    }, [pricingData]);


    useEffect(() => {
        if (pricingData) {
            setExtractionModel(findCheapestModel(extractionSummary, tokenModelsList, 'token'));
            setAnalysisModel(findCheapestModel(analysisSummary, tokenModelsList, 'token'));
            setMasterTutorialModel(findCheapestModel(masterTutorialSummary, tokenModelsList, 'token'));
            setStudentTutorialModel(findCheapestModel(studentTutorialSummary, tokenModelsList, 'token'));
            setScriptModel(findCheapestModel(scriptSummary, tokenModelsList, 'token'));
            setImageModel(findCheapestModel(imageSummary, imageModelsList, 'image'));
            setAudioModel(findCheapestModel(audioSummary, audioModelsList, 'audio'));
        }
    }, [
        pricingData, extractionSummary, analysisSummary, masterTutorialSummary, 
        studentTutorialSummary, scriptSummary, imageSummary, audioSummary,
        tokenModelsList, imageModelsList, audioModelsList
    ]);

    const extractionWhatIfCost = useMemo(() => calculateTokenCost(extractionModel, extractionSummary.totalInput, extractionSummary.totalOutput), [extractionModel, extractionSummary]);
    const analysisWhatIfCost = useMemo(() => calculateTokenCost(analysisModel, analysisSummary.totalInput, analysisSummary.totalOutput), [analysisModel, analysisSummary]);
    const masterTutorialWhatIfCost = useMemo(() => calculateTokenCost(masterTutorialModel, masterTutorialSummary.totalInput, masterTutorialSummary.totalOutput), [masterTutorialModel, masterTutorialSummary]);
    const studentTutorialWhatIfCost = useMemo(() => calculateTokenCost(studentTutorialModel, studentTutorialSummary.totalInput, studentTutorialSummary.totalOutput), [studentTutorialModel, studentTutorialSummary]);
    const scriptWhatIfCost = useMemo(() => calculateTokenCost(scriptModel, scriptSummary.totalInput, scriptSummary.totalOutput), [scriptModel, scriptSummary]);
    const imageWhatIfCost = useMemo(() => calculateImageCost(imageModel, imageSummary.totalImage), [imageModel, imageSummary]);
    const audioWhatIfCost = useMemo(() => calculateAudioCost(audioModel, audioSummary.totalChar), [audioModel, audioSummary]);

    const chartData = useMemo(() => ({
        labels: ['Extraction', 'Analysis', 'Master Tutorial', 'Student Tutorials', 'Slideshow Scripts', 'Slideshow Images', 'Slideshow Audio'],
        datasets: [
            {
                label: 'Actual Cost',
                data: [
                    extractionSummary.totalCost,
                    analysisSummary.totalCost,
                    masterTutorialSummary.totalCost,
                    studentTutorialSummary.totalCost,
                    scriptSummary.totalCost,
                    imageSummary.totalCost,
                    audioSummary.totalCost
                ],
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
            },
            {
                label: 'What-If Cost',
                data: [
                    extractionWhatIfCost,
                    analysisWhatIfCost,
                    masterTutorialWhatIfCost,
                    studentTutorialWhatIfCost,
                    scriptWhatIfCost,
                    imageWhatIfCost,
                    audioWhatIfCost
                ],
                backgroundColor: 'rgba(53, 162, 235, 0.5)',
            },
        ],
    }), [
        extractionSummary, analysisSummary, masterTutorialSummary, studentTutorialSummary, scriptSummary, imageSummary, audioSummary,
        extractionWhatIfCost, analysisWhatIfCost, masterTutorialWhatIfCost, studentTutorialWhatIfCost, scriptWhatIfCost, imageWhatIfCost, audioWhatIfCost
    ]);

    const totalActualCost = useMemo(() => {
        return [
            extractionSummary.totalCost,
            analysisSummary.totalCost,
            masterTutorialSummary.totalCost,
            studentTutorialSummary.totalCost,
            scriptSummary.totalCost,
            imageSummary.totalCost,
            audioSummary.totalCost
        ].reduce((acc, cost) => acc + (cost || 0), 0);
    }, [extractionSummary, analysisSummary, masterTutorialSummary, studentTutorialSummary, scriptSummary, imageSummary, audioSummary]);
    
    const totalWhatIfCost = useMemo(() => {
        return [
            extractionWhatIfCost,
            analysisWhatIfCost,
            masterTutorialWhatIfCost,
            studentTutorialWhatIfCost,
            scriptWhatIfCost,
            imageWhatIfCost,
            audioWhatIfCost
        ].reduce((acc, cost) => acc + (cost || 0), 0);
    }, [extractionWhatIfCost, analysisWhatIfCost, masterTutorialWhatIfCost, studentTutorialWhatIfCost, scriptWhatIfCost, imageWhatIfCost, audioWhatIfCost]);


    if (loading) return <p style={{ padding: '20px' }}>Loading cost data...</p>;
    if (error) return <p style={{ color: 'red', padding: '20px' }}>{error}</p>;

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <Link to={`/homework/${homeworkId}`}>← Back to Homework Detail</Link>
            <h1 style={{ marginTop: '20px' }}>Cost "What-If" Analysis</h1>
            
            {pricingData && (
              <CostBarChart 
                chartData={chartData} 
                totalActual={totalActualCost}
                totalWhatIf={totalWhatIfCost}
              />
            )}

            {pricingData && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' }}>
                    <WhatIfCostBox 
                        title="Extraction Summary"
                        summary={extractionSummary}
                        type="token"
                        models={tokenModelsList} 
                        selectedModel={extractionModel}
                        onModelChange={setExtractionModel}
                        whatIfCost={extractionWhatIfCost}
                    />
                    <WhatIfCostBox 
                        title="Analysis Summary"
                        summary={analysisSummary}
                        type="token"
                        models={tokenModelsList}
                        selectedModel={analysisModel}
                        onModelChange={setAnalysisModel}
                        whatIfCost={analysisWhatIfCost}
                    />
                    <WhatIfCostBox 
                        title="Master Tutorial"
                        summary={masterTutorialSummary}
                        type="token"
                        models={tokenModelsList}
                        selectedModel={masterTutorialModel}
                        onModelChange={setMasterTutorialModel}
                        whatIfCost={masterTutorialWhatIfCost}
                    />
                    <WhatIfCostBox 
                        title="Student Tutorials"
                        summary={studentTutorialSummary}
                        type="token"
                        models={tokenModelsList}
                        selectedModel={studentTutorialModel}
                        onModelChange={setStudentTutorialModel}
                        whatIfCost={studentTutorialWhatIfCost}
                    />
                    <WhatIfCostBox 
                        title="Slideshow Scripts"
                        summary={scriptSummary}
                        type="token"
                        models={tokenModelsList}
                        selectedModel={scriptModel}
                        onModelChange={setScriptModel}
                        whatIfCost={scriptWhatIfCost}
                    />
                    <WhatIfCostBox 
                        title="Slideshow Images"
                        summary={imageSummary}
                        type="image"
                        models={imageModelsList}
                        selectedModel={imageModel}
                        onModelChange={setImageModel}
                        whatIfCost={imageWhatIfCost}
                    />
                    <WhatIfCostBox 
                        title="Slideshow Audio"
                        summary={audioSummary}
                        type="audio"
                        models={audioModelsList}
                        selectedModel={audioModel}
                        onModelChange={setAudioModel}
                        whatIfCost={audioWhatIfCost}
                    />
                </div>
            )}
        </div>
    );
};

export default HomeworkWhatIf;